import { FC, useEffect, useState } from 'react';
import clsx from 'clsx';
import format from 'date-fns/format';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { VersionTypeEnum, IVersion } from 'models/product';
import RestoreIcon from '@material-ui/icons/Restore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { getImageStyle, getItemStyle } from '../FillProductByImage';
import { downloadImage, getImageMetadata, IImage, ImageSizes } from 'components/images/ImageSorter';
import { resize128Image } from 'utils/images-ts';
import { isCloudinary } from 'utils/cloudinary-ts';
import { StyledButton } from 'components/images/ImageSorter.style';

interface IHistoryImage extends IImage {
  createdAt: number | null;
}

interface ImageCardProps {
  image: IHistoryImage;
  onRestore: (image: string) => void;
}

const ImageCard: FC<ImageCardProps> = ({ image, onRestore }) => {
  const [error, setError] = useState(true);

  const handleLimitFileName = (fileNameParam?: string) => {
    const fileName = fileNameParam?.split('?')?.[0];
    const MAX_LENGTH = 38;
    if (!fileName || fileName.length <= MAX_LENGTH) return fileName;

    const lastDot = fileName.lastIndexOf(".");

    if (lastDot <= 0 || lastDot >= fileName.length - 1) {
      return `${fileName.slice(0, MAX_LENGTH - 1)}…`;
    }

    const extension = fileName.slice(lastDot);
    const length = MAX_LENGTH - extension.length - 1;

    if (length <= 0) return `…${extension.slice(-(MAX_LENGTH - 1))}`;

    return `${fileName.slice(0, length)}…${extension}`;
  };

  return (
    <div key={image.pictureUrl} style={{...getItemStyle(), userSelect: 'inherit'}}>
      <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
        <img
          src={resize128Image(image.pictureUrl)}
          style={getImageStyle({})}
          alt="Not available"
          onClick={() => window.open(image.pictureUrl, '_blank')}
          onLoad={() => setError(false)}
        />
      </div>
      <Tooltip title={image.pictureUrl || ''}>
        <b style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{handleLimitFileName(image.fileName)}</b>
      </Tooltip>
      <ImageSizes image={image} />
      <div style={{ fontSize: 12 }}>
        {image.createdAt ? format(image.createdAt, 'yyyy-MM-dd') : '-'}
      </div>
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'end' }}>
        {isCloudinary(image.pictureUrl) && (
          <Button
            disabled={error}
            fullWidth
            size="small"
            color="primary"
            variant="contained"
            startIcon={<RestoreIcon fontSize="small" />}
            style={{ marginTop: 4 }}
            onClick={() => onRestore(image.pictureUrl || '')}
          >
            Restore
          </Button>
        )}
        <StyledButton onClick={() => downloadImage(image.pictureUrl)}>
          Download
        </StyledButton>
      </div>
    </div>
  )
}

interface ImageHistoryProps {
  sortedImages: IImage[];
  loading: boolean;
  versions: IVersion[];
  onTrigger: () => Promise<void>;
  onRestore: (image: string) => void;
}

const ImageHistory: FC<ImageHistoryProps> = ({ sortedImages, loading, versions, onTrigger, onRestore }) => {
  const [expanded, setExpanded] = useState(false);
  const [imageHistory, setImageHistory] = useState<IHistoryImage[]>([]);

  const handleExpand = async (_: any, isExpanded: boolean) => {
    setExpanded(isExpanded);
    if (isExpanded) onTrigger();
  };

  useEffect(() => {
    const resultMap = new Map<string, IHistoryImage>();
    const currentImageUrls = sortedImages.map(image => image.pictureUrl);

    versions
      .filter(version => [VersionTypeEnum.CREATE, VersionTypeEnum.UPDATE].includes(version.type))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .forEach(version => {
        const images: string[] = [];
        if (version.data?.image_src) images.push(version.data.image_src);
        version.data?.additionalImages?.forEach(image => images.push(image));

        images.forEach(src => {
          if (currentImageUrls.includes(src) || resultMap.has(src)) return;

          resultMap.set(src, {
            isExisting: true,
            id: src,
            pictureUrl: src,
            fileName: src.split('/').pop() ?? src,
            createdAt: version.createdAt ? Number(version.createdAt) : null,
          });
        });
      });

    let result = Array.from(resultMap.values());
    Promise.all(
      result.map(async (image) => {
        const mappedImage = await getImageMetadata(image);
        return { ...mappedImage, createdAt: image.createdAt };
      })
    ).then(setImageHistory);
  }, [versions, sortedImages]);

  return (
    <Accordion expanded={expanded} onChange={handleExpand}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography style={{ fontSize: 15 }}>Image History</Typography>
      </AccordionSummary>

      <AccordionDetails style={{ overflow: 'hidden' }}>
        <div className="dropzone-custom ready-only" style={{ maxWidth: '100%', minHeight: 160, cursor: 'default' }}>
          <div className={clsx('dropzone-text', loading && 'dropzone-loading')}>
            {loading ? (
              <span className="loader">
                <CircularProgress size={30} />
              </span>
            ) : (
              <>{!imageHistory?.length && <div>Inga historiska bilder hittade</div>}</>
            )}
          </div>

          <div style={{ maxWidth: '100%', overflow: 'auto' }}>
            <div className="list-items" onClick={event => event.stopPropagation()}>
              {imageHistory.map(image => (
                <ImageCard key={image.pictureUrl} image={image} onRestore={onRestore} />
              ))}
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default ImageHistory;
